import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, Eye, EyeOff, Sparkles, Building2 } from 'lucide-react';
import { useTheme } from '@evi/skins';
import { consumePendingDeepLink } from '@/lib/pendingDeepLink';
import { readPendingReferral } from '@/lib/pendingReferral';
import { FLEET_JOIN_CODE_STORAGE_KEY, normalizeFleetJoinCode, continueFleetInvite, readStashedFleetInviteToken, inviteJtiFromToken } from '@/pages/fleet/JoinFleetPage';
import {
  login,
  lookupBuildingByJoinCode,
  API_BASE_URL,
  type PublicBuildingSummary,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { AuthLayout } from '@/components/AuthLayout';
import { SwitchBuildingModal } from '@/components/ui/SwitchBuildingModal';
import { toast } from '@/store/toast';
import formStyles from '@/components/Form.module.css';
import { FormError, useFormError } from '@/components/ui/FormError';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  // No length gate on login (see LoginRequestSchema in @evi/shared): a
  // mirrored v1 password may be shorter than the 12 the register/reset
  // forms enforce for NEW passwords.
  password: z.string().min(1, 'Enter your password').max(256),
});
type FormValues = z.infer<typeof schema>;

interface Props { onNavigate: (path: string) => void }

/**
 * Map a failed-login error to a human, neutral message for the form.
 *
 * Never surfaces a raw status code or the server's verbatim string. Auth
 * failures (401/403, and any other unexpected 4xx) ALL collapse to one
 * neutral line -- a message that said "wrong password" vs "no such account"
 * would reveal whether the email exists, enabling account enumeration.
 * Mapping on the HTTP status (not the server's message) keeps that guarantee
 * even if the backend copy ever changes.
 *
 * ky raises HTTPError (name 'HTTPError', carries `.response`) for non-2xx,
 * TimeoutError for the request timeout, and a TypeError for a dropped
 * connection -- we branch on the error name so no ky import is needed.
 */
function humanLoginError(err: unknown): string {
  const name = err instanceof Error ? err.name : '';
  if (name === 'HTTPError') {
    const status = Number((err as { response?: { status?: number } }).response?.status) || 0;
    if (status === 429) return 'error.rateLimited';
    if (status >= 500) return 'error.serverProblem';
    // 403 is the driver-surface gate: a non-driver (admin) account signing in on
    // the PWA, which has no MFA UI. The caller already proved the credential (the
    // gate sits after password verification), so this specific, actionable copy
    // leaks nothing — same rationale as the admin-surface gate. Mirrors the
    // magic-link fix (9e2f0b0a) so a non-driver gets a next step, not the silent
    // mfa_required loop.
    if (status === 403) return 'error.twoFactor';
    // 401 (wrong password OR no matching account both land here) and any other
    // 4xx: one neutral line, so nothing leaks which half was wrong.
    return 'error.invalidCredentials';
  }
  if (name === 'TimeoutError' || name === 'TypeError') {
    return 'error.network';
  }
  return 'error.generic';
}

// The friend link's code (lib/pendingReferral.ts): an explicit ?ref= on this URL, else the code stashed when the
// link opened. The Google start below carries it so the Api can bind the friendship on the callback, for a new
// account and an existing one alike.

export function LoginPage({ onNavigate }: Props) {
  const { t } = useTranslation('auth');
  const setAuth = useAuthStore((s) => s.setAuth);
  // Theme-persistence rework — respect the user's server-saved
  // `active_theme_family` + `theme_mode` on login instead of force-
  // resetting to Initiative-dark. Replaces the prior "every login
  // resets to brand canonical" pattern per Warren's product call:
  // returning drivers expect their selected theme to survive a
  // logout-then-login cycle. New signups (RegisterPage) keep the
  // brand-canonical introduction since they have no saved
  // preference yet. ThemeProvider continues to persist the choice
  // to its own localStorage keys (`evi.theme`, `evi.mode`)
  // independent of the auth store.
  const { setThemeId, setMode } = useTheme();
  const [serverError, setServerError] = useFormError();
  const [showPw, setShowPw] = useState(false);

  // Optional join-code threaded through from /join → "I have an
  // EVI account" or from a direct /login?join_code=... link.
  // After a successful login we open the SwitchBuildingModal
  // pre-resolved with this code, so the user can confirm-or-skip
  // the tenant move inline.
  const [joinCode, setJoinCode] = useState<string | null>(null);
  // A fleet code parked by the signed-out /join-fleet landing. It has to
  // reach whichever way a NEW account gets created from here — the
  // footer's Create account link and the Google button — or the account
  // lands in the default tenant and the fleet join is refused with a
  // building-code instruction the driver cannot follow. Read once per
  // render; storage may be blocked, in which case there is nothing to carry.
  const stashedFleetCode = ((): string | null => {
    try {
      return normalizeFleetJoinCode(sessionStorage.getItem(FLEET_JOIN_CODE_STORAGE_KEY));
    } catch {
      return null;
    }
  })();
  // A building code on this page is the more specific signal and keeps
  // its existing precedence.
  const registerHref = joinCode
    ? `/register?code=${encodeURIComponent(joinCode)}`
    : stashedFleetCode
      ? `/register?fleet_code=${encodeURIComponent(stashedFleetCode)}`
      : '/register';
  const [joinBuilding, setJoinBuilding] = useState<PublicBuildingSummary | null>(null);
  const [showSwitchPrompt, setShowSwitchPrompt] = useState(false);
  // Phase 3b Item 2 — fleet invite handoff. Same shape as
  // RegisterPage: an older /join-fleet (the retired needs_login
  // branch) or a saved link lands here with email + pendingToken on
  // the URL; we pre-fill the email field and follow up the invite
  // after successful login.
  const [prefilledEmail, setPrefilledEmail] = useState<string | null>(null);
  const [pendingFleetToken, setPendingFleetToken] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('join_code') ?? '';
    const normalized = raw.trim().toUpperCase();
    if (normalized && normalized.length === 6) {
      setJoinCode(normalized);
      void lookupBuildingByJoinCode(normalized).then((result) => {
        if (result) setJoinBuilding(result);
      });
    }
    const emailQ = params.get('email');
    const tokenQ = params.get('pendingToken');
    if (emailQ) setPrefilledEmail(emailQ.toLowerCase());
    if (tokenQ) setPendingFleetToken(tokenQ);

    // The Google OAuth backend bounces failed or cancelled sign-ins
    // to /login?error=google. Surface a generic toast (visible on
    // auth screens now that <Toast /> mounts on every App branch)
    // and strip the param so a refresh doesn't re-toast. Generic on
    // purpose: the callback's reasons (denied consent, expired
    // state, unverified email) all read the same to the driver.
    // 'google_link': the first Google link to a never-verified password
    // account was refused because the notice email is not deliverable
    // (google.ts). Point at the two sign-ins that DO work, not "try again".
    const oauthError = params.get('error');
    if (oauthError === 'google' || oauthError === 'google_link') {
      toast(
        oauthError === 'google' ? t('toast.googleFailed') : t('toast.googleLinkNeedsPassword'),
        { tone: 'warn', ...(oauthError === 'google_link' ? { durationMs: 9000 } : {}) },
      );
      params.delete('error');
      const qs = params.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }
  }, []);

  // Pre-compute the join-target's display name for the inline
  // banner + page title. Mirrors the discriminated union used in
  // the modal/JoinPage.
  const joinTargetName: string | null = joinBuilding
    ? (joinBuilding.kind === 'location'
        ? joinBuilding.location.name
        : (joinBuilding.tenant.display_name ?? joinBuilding.tenant.name))
    : null;

  const { register, handleSubmit, formState: { errors, isSubmitting }, setValue } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (prefilledEmail) setValue('email', prefilledEmail);
  }, [prefilledEmail, setValue]);

  const onSubmit = async (values: FormValues) => {
    setServerError('');
    try {
      const res = await login(values);
      // Defensive mirror of the magic-link fix: a token-less 200 is the admin
      // MFA-pending shape (unreachable on the PWA now that the server gates
      // non-drivers with a 403, but never setAuth(undefined) and loop
      // /map -> /login regardless).
      if (!res.access_token) {
        setServerError(t('error.twoFactor'));
        return;
      }
      setAuth(res.user, res.access_token);
      // Respect the driver's server-saved theme preference instead
      // of force-resetting to Initiative-dark. If the server has no
      // saved theme for this user (new account, pre-migration
      // record), leave ThemeProvider's current state untouched —
      // it's already whatever the localStorage persistence
      // restored on app boot (typically `default`/`dark` for first-
      // time visitors, or the last-used theme for returning
      // drivers on this device).
      if (res.user.active_theme_family) {
        setThemeId(res.user.active_theme_family);
      }
      if (res.user.theme_mode) {
        setMode(res.user.theme_mode);
      }

      // Phase 3b Item 2 — follow up an email invite. A password sign-in
      // never joins a fleet (only the emailed sign-in link does), so a
      // pending_verification answer returns to /join-fleet, which tells
      // the driver how to finish and offers to send the link.
      if (pendingFleetToken) {
        try {
          if (await continueFleetInvite(pendingFleetToken, onNavigate)) return;
        } catch {
          // Fall through to the normal post-login routing.
        }
      }

      // Login succeeded. If we arrived with a join_code, the user
      // landed here from a /join link with the intent to switch
      // their existing account to a new building. Open the switch
      // modal instead of routing straight to the map.
      if (joinCode && joinBuilding) {
        setShowSwitchPrompt(true);
      } else {
        // Replay a stashed charger deep link (shared link / QR opened
        // while signed out) — else the map, the founder's default
        // lander; Profile stays one tap away on the third tab.
        onNavigate(consumePendingDeepLink() ?? '/map');
      }
    } catch (err) {
      setServerError(t(humanLoginError(err)));
    }
  };

  return (
    <>
    <AuthLayout
      title={joinTargetName
        ? t('login.titleSwitch', { building: joinTargetName })
        : t('login.title')}
      subtitle={joinTargetName
        ? t('login.subtitleSwitch')
        : t('login.subtitle')}
      footer={<>{t('login.footerPrompt')} <a href={registerHref} onClick={(e) => { e.preventDefault(); onNavigate(registerHref); }}>{t('login.footerLink')}</a></>}
    >
      {/* Entry order leads with the no-password paths: magic link
          first, Google second (Apple stays flag-gated in the same
          cluster), then the email/password form below the divider. */}
      <button
        type="button"
        className={formStyles.btnSecondary}
        onClick={() => onNavigate('/auth/magic')}
      >
        <Sparkles size={16} /> {t('login.magicLink')}
      </button>

      {/* Backend-initiated Google OAuth — a FULL-PAGE navigation to
          the API's /auth/google (not window.open: the OAuth redirect
          chain leaves this page and returns via the magic-link
          handoff). The API signs the state, sends the browser to
          Google, and the callback bounces back signed-in — or to
          /login?error=google, surfaced by the mount effect above. */}
      <button
        type="button"
        className={formStyles.btnSecondary}
        onClick={() => {
          const ref = readPendingReferral();
          // A fleet code parked by /join-fleet rides the OAuth start the
          // same way the referral does, so a Google-created account is
          // placed in the fleet's building instead of the default tenant.
          const qs = new URLSearchParams();
          if (ref) qs.set('ref', ref);
          if (stashedFleetCode) qs.set('fleet_code', stashedFleetCode);
          // A fleet invite link keeps working through Google: the server
          // joins a NEW account at creation, and records a pending join on
          // an existing one that this very sign-in then completes. Only the
          // invite's id goes in this URL, never the token itself.
          const inviteJti = inviteJtiFromToken(pendingFleetToken ?? readStashedFleetInviteToken());
          if (inviteJti) qs.set('fleet_invite', inviteJti);
          const q = qs.toString();
          window.location.href = q
            ? `${API_BASE_URL}/auth/google?${q}`
            : `${API_BASE_URL}/auth/google`;
        }}
      >
        <GoogleIcon /> {t('login.google')}
      </button>

      {/*
       * Apple Sign-In button is env-flag-gated. Default off
       * (VITE_APPLE_SIGNIN_ENABLED unset or anything other than
       * 'true') so it doesn't render until the Apple Developer
       * setup is complete + the .p8 private key is configured in
       * the API env for the auth/apple/callback handler.
       *
       * Toggle on per environment by setting
       * VITE_APPLE_SIGNIN_ENABLED=true in apps/web/.env. The
       * AppleIcon component below + any backend stubs are NOT
       * removed — flip the flag when the config lands.
       */}
      {import.meta.env['VITE_APPLE_SIGNIN_ENABLED'] === 'true' && (
        <button
          type="button"
          className={formStyles.btnSecondary}
          onClick={() => toast(t('toast.appleSoon'), { tone: 'neutral' })}
        >
          <AppleIcon /> {t('login.apple')}
        </button>
      )}

      <div className={formStyles.divider}>{t('login.divider')}</div>

      <form className={formStyles.form} onSubmit={handleSubmit(onSubmit)}>
        {joinTargetName && (
          <div className={formStyles.formSuccess} role="status">
            <Building2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {t('login.switchBanner')} <strong>{joinTargetName}</strong>
          </div>
        )}
        {serverError && <FormError announce={serverError.nonce}>{serverError.message}</FormError>}

        <div className={formStyles.field}>
          <label className={formStyles.label}><Mail size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {t('login.emailLabel')}</label>
          <input
            className={formStyles.input}
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            {...register('email')}
          />
          {errors.email && <span className={formStyles.errorMsg}>{errors.email.message}</span>}
        </div>

        <div className={formStyles.field}>
          <label className={formStyles.label}><Lock size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {t('login.passwordLabel')}</label>
          <div style={{ position: 'relative' }}>
            <input
              className={formStyles.input}
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••••••"
              {...register('password')}
              style={{ paddingRight: 44 }}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--color-tx-m)', cursor: 'pointer', padding: 6 }}
              aria-label={showPw ? t('login.hidePassword') : t('login.showPassword')}
            >
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && <span className={formStyles.errorMsg}>{errors.password.message}</span>}
        </div>

        <div style={{ textAlign: 'right', marginTop: -4 }}>
          <a
            href="/auth/forgot"
            onClick={(e) => { e.preventDefault(); onNavigate('/auth/forgot'); }}
            style={{ fontSize: 13, color: 'var(--color-tx-m)' }}
          >
            {t('login.forgotPassword')}
          </a>
        </div>

        <button type="submit" className={formStyles.btnPrimary} disabled={isSubmitting}>
          {isSubmitting ? t('login.submitting') : t('login.submit')}
        </button>

      </form>
    </AuthLayout>

    {showSwitchPrompt && joinCode && joinBuilding && (
      <SwitchBuildingModal
        initialCode={joinCode}
        initialBuilding={joinBuilding}
        // currentBuilding stays null on this surface. The /me/account
        // fetch hasn't resolved yet right after login, and the modal
        // treats null as the safest case (first-join framing) so
        // the user never sees a switch state with a "your current
        // building" placeholder. If we want exact-tenant copy here
        // we can wire a fetchAccount call via a useQuery and pass
        // the result; today's UX matches the schema-honest framing
        // either way.
        currentBuilding={null}
        onNavigate={onNavigate}
        onClose={() => {
          setShowSwitchPrompt(false);
          onNavigate('/profile');
        }}
      />
    )}
    </>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 5.04c1.61 0 3.05.56 4.18 1.66l3.13-3.13C17.46 1.74 14.97.66 12 .66 7.43.66 3.49 3.27 1.7 7l3.66 2.83c.86-2.6 3.27-4.79 6.64-4.79z" />
      <path fill="#34A853" d="M23.34 12.27c0-.79-.07-1.55-.2-2.27H12v4.51h6.43c-.28 1.5-1.13 2.78-2.4 3.62l3.69 2.86c2.16-2 3.62-4.93 3.62-8.72z" />
      <path fill="#4A90E2" d="M5.36 14.21c-.22-.66-.34-1.36-.34-2.08s.12-1.42.34-2.08L1.7 7.21C.97 8.66.55 10.29.55 12s.42 3.34 1.15 4.79l3.66-2.58z" />
      <path fill="#FBBC05" d="M12 23.34c2.97 0 5.46-.98 7.28-2.66l-3.69-2.86c-1.02.69-2.34 1.1-3.59 1.1-3.37 0-6.22-2.2-7.24-5.13l-3.66 2.58c1.79 3.73 5.73 6.97 10.9 6.97z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M16.36 12.4c.04-3.65 2.97-5.4 3.1-5.49-1.7-2.48-4.34-2.82-5.27-2.85-2.24-.23-4.39 1.32-5.52 1.32-1.16 0-2.91-1.3-4.79-1.26-2.45.04-4.74 1.43-6.01 3.62-2.59 4.49-.66 11.09 1.84 14.74 1.25 1.79 2.71 3.78 4.59 3.71 1.85-.07 2.55-1.19 4.78-1.19s2.86 1.19 4.81 1.16c2-.04 3.25-1.79 4.46-3.6 1.43-2.06 2.01-4.07 2.04-4.18-.05-.02-3.92-1.5-3.96-5.95zM13.06 2.5c1.02-1.23 1.7-2.95 1.51-4.66-1.46.06-3.23.97-4.28 2.2-.94 1.09-1.77 2.83-1.55 4.51 1.63.13 3.3-.83 4.32-2.05z"/>
    </svg>
  );
}
